import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from '../../api/axiosConfig';
import _ from 'lodash';
import { useAuth } from '../../context/AuthContext';

import icon from '../../assets/community.svg';
import uploadBtn from '../../assets/fileUpload.svg';
import clip from '../../assets/clip.svg';
import fileIcon from '../../assets/fileIcon.svg';
import closeIcon from '../../assets/closeIcon.svg';

interface QnAFormData {
  user: string;
  title: string;
  content: string;
  category: string;
  files: File[];
}

interface QnAProps {
  mode: 'create' | 'edit';
  onEditSuccess?: () => void; // 수정 완료 시 호출
}

interface ExistingFile {
  originalName: string;
  _id: string;
}

const QnA: React.FC<QnAProps> = ({ mode, onEditSuccess }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const fileLimit = 5;
  const [newFiles, setNewFiles] = useState<File[]>([]); // 새로 선택한 파일
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]); // 서버에서 가져온 기존 파일
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]); // 삭제 예정 기존 파일 ID
  const [fileCount, setFileCount] = useState<number>(0);

  const quillRef = useRef<ReactQuill | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QnAFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      user: user?.id || '',
      title: '',
      content: '',
      category: 'general',
    },
  });

  // ReactQuill 이미지 붙여넣기/드래그 방지
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 4) 클립보드 매처로 IMG 무시 (이중 안전장치)
    const Delta = Quill.import('delta');
    editor.clipboard.addMatcher('IMG', () => new Delta());

    // 2) Ctrl+V로 이미지 들어오는 것 차단
    const root = editor.root as HTMLElement;
    const onPaste = (e: ClipboardEvent) => {
      const hasImage =
        !!e.clipboardData &&
        Array.from(e.clipboardData.items).some(
          item => item.kind === 'file' && item.type.startsWith('image/')
        );
      if (hasImage) {
        e.preventDefault();
      }
    };

    // 3) 드래그&드롭 이미지 차단
    const onDrop = (e: DragEvent) => {
      const hasImage =
        !!e.dataTransfer && Array.from(e.dataTransfer.files).some(f => f.type.startsWith('image/'));
      if (hasImage) {
        e.preventDefault();
      }
    };

    root.addEventListener('paste', onPaste);
    root.addEventListener('drop', onDrop);

    return () => {
      root.removeEventListener('paste', onPaste);
      root.removeEventListener('drop', onDrop);
    };
  }, []);

  useEffect(() => {
    if (mode === 'edit' && id) {
      const fetchQnADetail = async () => {
        try {
          const response = await axios.get(`/api/qna/${id}`);
          const data = response.data;
          reset({
            user: data.user,
            title: data.title,
            content: data.content,
            category: data.category || 'general',
            files: [],
          });
          setExistingFiles(data.fileList || []);
        } catch (err) {
          console.error('Error fetching QnA detail:', err);
        }
      };
      fetchQnADetail();
    }
  }, [mode, id, reset]);

  // ReactQuill 내용 검증 함수
  const validateQuillContent = (value: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = value;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0 || '문의 내용을 입력해주세요';
  };

  // 파일 삭제 핸들러
  const handleNewFileRemove = (index: number) => {
    const updated = [...newFiles];
    updated.splice(index, 1);
    setNewFiles(updated);
  };

  // 기존 파일 삭제 핸들러
  const handleExistingFileRemove = (id: string) => {
    setExistingFiles(prev => prev.filter(f => f._id !== id)); // UI에서 제거
    setDeletedFileIds(prev => [...prev, id]);
  };

  // 파일 입력 변경
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const combined = [...newFiles, ...files].slice(0, fileLimit);
    setNewFiles(combined);
  };

  const onSubmit = async (data: QnAFormData) => {
    try {
      let uploadedIds: string[] = [];

      // 1) 새 파일 업로드
      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach(file => formData.append('files', file));
        formData.append('usedFor', 'qna');

        const uploadRes = await axios.post(`/api/upload-files/${'qna'}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedIds = uploadRes.data.fileIds;
      }

      // 2) 모든 파일 ID 합치기 (기존 + 새 업로드)
      const allFileIds = [...existingFiles.map(f => f._id), ...uploadedIds];

      // 3) 삭제된 파일 DB 처리 (deleted:true)
      if (deletedFileIds.length > 0) {
        deletedFileIds.forEach(async id => {
          try {
            await axios.put(`/api/upload-files/mark-deleted/${id}`, { deleted: true });
          } catch (err) {
            console.error(`Error deleting file ${id}:`, err);
          }
        });
      }

      // 4) QnA 등록/수점
      const payload = {
        user: user?.id || '',
        category: data.category || 'general',
        title: data.title,
        content: data.content,
        files: allFileIds,
      };

      if (mode === 'edit' && id) {
        await axios.put(`/api/qna/${id}`, payload);
        alert('문의가 수정되었습니다.');
        onEditSuccess?.();
        navigate(`/setting/qna/${id}`);
      } else {
        await axios.post('/api/qna/create', payload);
        alert('문의가 등록되었습니다.');
        onEditSuccess?.();
        navigate('/setting/qna-list');
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setFileCount(existingFiles.length + newFiles.length);
  }, [existingFiles, newFiles]);

  useEffect(() => {
    // 사용자가 선택한 첨부파일 총 개수가 파일 제한 개수를 초과하면 경고 메시지 표시
    if (fileCount > fileLimit) {
      alert('첨부파일은 최대 ' + fileLimit + '개까지 선택 가능합니다.');
    }
  }, [fileCount, fileLimit]);

  return (
    <div className="qna-container">
      <div className="page-header">
        <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
        <h2 className="page-header__title">{mode === 'edit' ? '문의 수정' : '문의하기'}</h2>
        <p>궁금한 점이나 문제가 있으시면 언제든지 문의해주세요.</p>
      </div>

      <form className="qna-container__form" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label htmlFor="category">문의 유형</label>
          <select id="category" {...register('category', { required: '문의 유형을 선택해주세요' })}>
            <option value="general">일반 문의</option>
            <option value="technical">기술 문의</option>
            <option value="bug">버그 신고</option>
            <option value="feature">기능 요청</option>
            <option value="account">계정 문의</option>
          </select>
          {errors.category && <span className="error-message">{errors.category.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="title">제목</label>
          <input
            type="text"
            id="title"
            {...register('title', { required: '제목을 입력해주세요' })}
            placeholder="문의 제목을 입력해주세요"
          />
          {errors.title && <span className="error-message">{errors.title.message}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="content">문의 내용</label>
          <div translate="no">
            <Controller
              name="content"
              control={control}
              rules={{ validate: validateQuillContent }}
              render={({ field }) => (
                <ReactQuill
                  id="content"
                  className="quill-input"
                  theme="snow"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="문의 내용을 자세히 입력해주세요"
                  ref={quillRef}
                />
              )}
            />
          </div>
          {errors.content && <span className="error-message">{errors.content.message}</span>}
        </div>

        <div className="form-group">
          <div className="file-label-group">
            <label htmlFor="files">첨부파일</label>
            <span className="file-count">
              {fileCount}/{fileLimit}
            </span>
          </div>

          {fileCount < fileLimit && (
            <>
              <input
                type="file"
                id="files"
                className="file-input"
                multiple
                onChange={handleFileChange}
              />
              <label htmlFor="files" className="file-upload-btn">
                <div>
                  <img src={uploadBtn} alt="파일업로드" />
                  <span> {fileCount > 0 ? '파일 추가' : '파일 선택'}</span>
                </div>
                <img src={clip} alt="클립" />
              </label>
            </>
          )}

          <div className="file-list">
            {existingFiles.map(file => (
              <div className="file-item" key={file._id}>
                <span className="file-item-name">
                  <img src={fileIcon} alt="업로드 파일" />
                  {file.originalName}
                </span>
                <span
                  className="file-item-remove"
                  onClick={() => handleExistingFileRemove(file._id)}
                >
                  <img src={closeIcon} alt="파일 제거" />
                </span>
              </div>
            ))}

            {newFiles.map((file, index) => (
              <div className="file-item" key={file.name + index}>
                <span className="file-item-name">
                  <img src={fileIcon} alt="업로드 파일" />
                  {file.name}
                </span>
                <span className="file-item-remove" onClick={() => handleNewFileRemove(index)}>
                  <img src={closeIcon} alt="파일 제거" />
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          {mode === 'edit' ? (
            <button
              type="button"
              className="back-btn"
              onClick={() => {
                navigate(`/setting/qna/${id}`);
                onEditSuccess?.();
              }}
            >
              취소
            </button>
          ) : (
            <button
              type="button"
              className="back-btn"
              onClick={() => {
                navigate(`/setting`);
                onEditSuccess?.();
              }}
            >
              취소
            </button>
          )}
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? <LoadingSpinner /> : mode === 'edit' ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

const LoadingSpinner = () => {
  return <div className="loading-spinner"></div>;
};

export default QnA;
